import React, { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createWSClient, wsLink } from '@trpc/client';
import { SuperJSON } from 'superjson';
import { singletonHook } from 'react-singleton-hook';
import { trpc } from '@duke-hq/svc-support-web/trpc';

import { type ChatActions, ChatContext } from './Context';
import { ChatConnectionProvider, trackConnection } from './ChatConnection';

function getProtocols() {
  let httpProtocol = 'http://';
  let wsProtocol = 'ws://';
  if (window.location.protocol === 'https:') {
    httpProtocol = 'https://';
    wsProtocol = 'wss://';
  }
  return { httpProtocol, wsProtocol };
}

type init = {
  loading: boolean;
  queryClient?: QueryClient;
  trpcClient?: ReturnType<typeof trpc.createClient>;
  connection?: ReturnType<typeof trackConnection>;
};

const useSupportTrpcClientImpl = () => {
  const [data, setData] = useState<init>({
    loading: false,
    queryClient: undefined,
    trpcClient: undefined,
  });

  useEffect(() => {
    const url = `${getProtocols().wsProtocol}${window.location.host}/api/support`;
    const wsClient = createWSClient({
      url,
      retryDelayMs: (attemptIndex: number) => {
        return attemptIndex === 0 ? 0 : Math.min(1000 * 2 ** attemptIndex, 30000);
      },
      // The client lives for the whole session, so lazy mode stops it holding a
      // socket after the chat closes. 10s keeps it from one chat to the next.
      lazy: { enabled: true, closeMs: 10_000 },
    });

    setData((data) => ({
      ...data,
      loading: false,
      queryClient: new QueryClient(),
      trpcClient: trpc.createClient({
        links: [wsLink({ client: wsClient, transformer: SuperJSON })],
      }),
      connection: trackConnection(wsClient),
    }));
  }, []);

  return data;
};

type UseSupportTrpc = ReturnType<typeof useSupportTrpcClientImpl>;

const useSupportTrpcClient = singletonHook<UseSupportTrpc>(
  { loading: true, queryClient: undefined, trpcClient: undefined },
  useSupportTrpcClientImpl,
);

// This component provides the trpc client for the support chat service.
//
// singletonHook cannot be rendered server-side we need to wrap up the
// client setup into a component that is lazy loaded only on the client side.
function ClientOnlyChatProvider({
  children,
  chatActions,
  fallback = <div>Loading</div>,
}: {
  chatActions: ChatActions;
  children?: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const { loading, queryClient, trpcClient, connection } = useSupportTrpcClient();

  if (loading) {
    return fallback;
  }

  if (!queryClient || !trpcClient || !connection) {
    // an error occurred
    return <div>Failed to load support chat</div>;
  }

  return (
    <ChatContext.Provider value={chatActions}>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <ChatConnectionProvider store={connection}>{children}</ChatConnectionProvider>
        </QueryClientProvider>
      </trpc.Provider>
    </ChatContext.Provider>
  );
}

export default ClientOnlyChatProvider;
